<script setup lang="ts">
import * as THREE from 'three'
import { computed, watch, ref, onUnmounted } from 'vue'
//Components
import DroneSpinner from 'Common/components/elements/feedback/DroneSpinner.vue'
//Classes
import { VisualizerScene } from 'Common/components/elements/visualizer/visualizerScene'
//API
import { HeatmapMarkerType } from 'Common/types/elements/visualizer/marker'

interface PropTypes {
  aircraft: { stlUrl?: string }
  defects: any[]
}

const props = defineProps<PropTypes>()

const sceneLoaded = ref<boolean>(false)
const rootRef = ref<HTMLElement | null>(null)
const visualizerParent = ref<HTMLElement | null>(null)
const labelRef = ref<HTMLElement | null>(null)

const isLoading = computed(() => !sceneLoaded.value)

let visualizerScene: VisualizerScene | null = null

function sceneLoadedCallback() {
  if (!visualizerScene) return
  sceneLoaded.value = true

  // set the camera to be looking from above and down onto the nose of the plane
  visualizerScene.camera.position.set(0, 0.35, 1.05)
  visualizerScene.camera.fitCameraToSelection(
    [visualizerScene.scene.getObjectByName('root')],
    visualizerScene.controls,
    {
      // set an offset of the camera's focal point closer to the front
      // we do this to compenstate for the target symbol positioned around 1/4 of the planes length
      centerOffset: new THREE.Vector3(1, 1, 0.75),
      // set a margin or buffer so the plane doesnt bleed off the edge
      fitOffset: 0.625,
    }
  )
}

function generateHeatmapMarkers(defects) {
  // Guard against running before the scene or data is ready
  if (!visualizerScene || !defects?.length) return

  const heatmapMarkersArray = []
  defects.forEach((defect) => {
    const hmInfo: HeatmapMarkerType = {
      locx: defect.defectCentroid.x,
      locy: defect.defectCentroid.y,
      locz: defect.defectCentroid.z,
      heading: 0,
      cameraangle: 0,
      name: defect.imageUuid,
      severity: defect.annotationType,
    }
    heatmapMarkersArray.push(hmInfo)
  })
  const selectedMarker = heatmapMarkersArray[0]
  visualizerScene.initializeGroup('heatmap', heatmapMarkersArray, () => null, selectedMarker)
}

// This single watcher manages the entire lifecycle of the visualizer scene,
// replacing the need for onMounted and a separate URL watcher.
watch(
  () => props.aircraft?.stlUrl,
  (newUrl) => {
    // If a scene already exists, destroy it before creating a new one
    if (visualizerScene) {
      visualizerScene.destroy()
      visualizerScene = null
    }

    // If a new URL is provided and the DOM is ready, initialize the scene
    if (newUrl && visualizerParent.value && rootRef.value) {
      sceneLoaded.value = false // Reset loading state
      visualizerScene = new VisualizerScene(newUrl, {
        onLoadedCallback: sceneLoadedCallback,
        sceneOptions: {
          ambientLightOptions: { intensity: 2 },
          hideGrid: true,
          hideMeshPlane: true,
        },
      })
      visualizerScene.init(visualizerParent.value, rootRef.value)
    }
  },
  { immediate: true } // 'immediate' ensures this runs on component mount
)

// This watcher now safely updates markers only when defects change
watch(
  () => props.defects,
  (defects) => {
    // Only attempt to generate markers if the 3D scene has finished loading
    if (sceneLoaded.value) {
      generateHeatmapMarkers(defects)
    }
  },
  { immediate: true, deep: true }
)

onUnmounted(() => {
  if (visualizerScene) {
    visualizerScene.destroy()
    visualizerScene = null
  }
  rootRef.value = null
  visualizerParent.value = null
  labelRef.value = null
})
</script>

<template>
  <div class="h-full w-full relative">
    <template v-if="isLoading">
      <div
        id="inspectionImg"
        class="absolute top-0 h-full w-full text-white bg-neutral-900 flex flex-col items-center justify-center px-1 pb-5 z-10"
      >
        <DroneSpinner />
        <span>Loading Heatmap Visualizer Data</span>
      </div>
    </template>
    <div id="visualizer-parent" ref="visualizerParent" class="h-full">
      <div id="visualizer-root" ref="rootRef" class="h-full w-full relative">
        <div
          ref="labelRef"
          class="absolute hidden top-[50%] left-[50%] h-28 w-min bg-black bg-opacity-75 text-white z-50 rounded"
        ></div>
      </div>
    </div>
  </div>
</template>
